import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { formatPhoneForDisplay } from '../utils/phone';

function CustomerReport({ token }) {
  const [events, setEvents] = useState([]);
  const [jobCoas, setJobCoas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  // Filters
  const [filterJobType, setFilterJobType] = useState('');
  const [filterPaymentStatus, setFilterPaymentStatus] = useState('');
  const [filterJobStatus, setFilterJobStatus] = useState('');
  const [filterZipCode, setFilterZipCode] = useState('');
  const [filterHasAttachments, setFilterHasAttachments] = useState('');
  const [filterFromDate, setFilterFromDate] = useState('');
  const [filterToDate, setFilterToDate] = useState('');
  const [exporting, setExporting] = useState(false);
  const canExportQuickbooksCsv = true;

  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        setNotice('');
        const [eventsRes, coasRes] = await Promise.all([
          axios.get(`/events`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`/job-coas`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        
        setEvents(eventsRes.data.events || []);
        setJobCoas(coasRes.data || []);
      } catch (err) {
        console.error('Error fetching report data:', err);
        setError('Failed to load report data');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [token]);

  // Helper to get Job Type description
  const getJobDescription = (id) => {
    const coa = jobCoas.find(c => String(c.coa_id) === String(id));
    return coa ? coa.description : 'Unknown';
  };

  // Helper for status labels
  const PAYMENT_STATUS_LABELS = {
    0: 'Unpaid',
    1: 'Pending',
    2: 'Paid'
  };

  const JOB_STATUS_LABELS = {
    0: 'Scheduled',
    2: 'Completed',
    3: 'Cancelled',
    4: 'Allen',
    5: 'Note',
    6: 'Reschedule'
  };

  // Filter logic
  const filteredEvents = events.filter(ev => {
    if (filterFromDate && ev.date && ev.date < filterFromDate) return false;
    if (filterToDate && ev.date && ev.date > filterToDate) return false;

    // Job Type Filter
    if (filterJobType && String(ev.job_number) !== filterJobType) return false;
    
    // Payment Status Filter
    if (filterPaymentStatus && String(ev.payment_status) !== filterPaymentStatus) return false;
    
    // Job Status Filter
    if (filterJobStatus && String(ev.job_status) !== filterJobStatus) return false;
    
    // Zip Code Filter (partial match)
    if (filterZipCode && (!ev.addr_zip_code || !ev.addr_zip_code.includes(filterZipCode))) return false;

    const attachmentCount = Number(ev.attachment_count || 0);
    if (filterHasAttachments === 'yes' && attachmentCount <= 0) return false;
    if (filterHasAttachments === 'no' && attachmentCount > 0) return false;

    return true;
  });

  // Helper to format phone number
  const formatPhoneNumber = (phone) => {
    if (!phone) return '-';
    return formatPhoneForDisplay(phone);
  };

  // Calculate totals
  const totalRevenue = filteredEvents.reduce((sum, ev) => sum + (Number(ev.negotiated_price) || 0), 0);

  const handleQuickbooksExport = async () => {
    if (!canExportQuickbooksCsv) {
      setNotice('QuickBooks CSV export is disabled for this tenant plan.');
      return;
    }
    try {
      setExporting(true);
      setNotice('');
      const params = new URLSearchParams();
      if (filterJobType) params.set('jobType', filterJobType);
      if (filterPaymentStatus) params.set('paymentStatus', filterPaymentStatus);
      if (filterJobStatus) params.set('jobStatus', filterJobStatus);
      if (filterZipCode) params.set('zipCode', filterZipCode);
      if (filterFromDate) params.set('fromDate', filterFromDate);
      if (filterToDate) params.set('toDate', filterToDate);

      const qs = params.toString();
      const routePath = '/reports/quickbooks-export.csv';
      const querySuffix = qs ? `?${qs}` : '';
      const configuredBackendBase = String(process.env.REACT_APP_BACKEND_BASE_URL || '').trim().replace(/\/$/, '');
      const fallbackBases = [
        configuredBackendBase,
        `${window.location.protocol}//${window.location.hostname}:3002`,
        `${window.location.protocol}//${window.location.hostname}:3001`
      ].filter(Boolean);

      const candidateUrls = [
        `/api${routePath}${querySuffix}`,
        ...fallbackBases.map((base) => `${base}${routePath}${querySuffix}`)
      ];

      const looksLikeHtml = async (blob, contentType) => {
        const ct = String(contentType || '').toLowerCase();
        if (ct.includes('text/html')) return true;
        if (!blob || typeof blob.text !== 'function') return false;
        const preview = (await blob.text()).slice(0, 300).toLowerCase();
        return preview.includes('<!doctype html') || preview.includes('<html');
      };

      let response;
      let lastError;
      for (const url of candidateUrls) {
        try {
          const attempt = await axios.get(url, {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          });

          const contentType = attempt.headers?.['content-type'] || '';
          if (await looksLikeHtml(attempt.data, contentType)) {
            lastError = new Error('Export endpoint returned HTML instead of CSV');
            continue;
          }

          response = attempt;
          break;
        } catch (err) {
          lastError = err;
        }
      }

      if (!response) {
        throw lastError || new Error('No export endpoint returned CSV data');
      }

      const downloadUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv;charset=utf-8;' }));
      const link = document.createElement('a');
      const disposition = response.headers?.['content-disposition'] || '';
      const nameMatch = disposition.match(/filename="?([^";]+)"?/i);
      link.href = downloadUrl;
      link.setAttribute('download', nameMatch?.[1] || `quickbooks-export-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (err) {
      console.error('QuickBooks export failed:', err);
      if (err?.response?.status === 403) {
        setNotice('QuickBooks CSV export is disabled for this tenant plan.');
      } else {
        setNotice('Failed to export QuickBooks CSV. API route returned non-CSV response.');
      }
    } finally {
      setExporting(false);
    }
  };

  if (loading) return <div>Loading report...</div>;
  if (error) return <div style={{color: 'red'}}>{error}</div>;

  return (
    <div style={{ padding: '20px' }}>
      <h2>Customer Report</h2>
      
      {/* Filters */}
      <div style={{ 
        display: 'flex', 
        gap: '15px', 
        marginBottom: '20px', 
        padding: '15px', 
        background: '#f5f5f5', 
        borderRadius: '5px',
        flexWrap: 'wrap'
      }}>
        <div>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Job Type</label>
          <select value={filterJobType} onChange={e => setFilterJobType(e.target.value)} style={{ padding: '5px' }}>
            <option value="">All Job Types</option>
            {jobCoas.map(coa => (
              <option key={coa.coa_id} value={String(coa.coa_id)}>{coa.description}</option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Payment Status</label>
          <select value={filterPaymentStatus} onChange={e => setFilterPaymentStatus(e.target.value)} style={{ padding: '5px' }}>
            <option value="">All Payment Statuses</option>
            <option value="0">Unpaid</option>
            <option value="1">Pending</option>
            <option value="2">Paid</option>
          </select>
        </div>

        <div>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Job Status</label>
          <select value={filterJobStatus} onChange={e => setFilterJobStatus(e.target.value)} style={{ padding: '5px' }}>
            <option value="">All Job Statuses</option>
            <option value="0">Scheduled</option>
            <option value="1">In Progress</option>
            <option value="2">Completed</option>
            <option value="3">Cancelled</option>
          </select>
        </div>

        <div>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>From Date</label>
          <input
            type="date"
            value={filterFromDate}
            onChange={e => setFilterFromDate(e.target.value)}
            style={{ padding: '5px' }}
          />
        </div>

        <div>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>To Date</label>
          <input
            type="date"
            value={filterToDate}
            onChange={e => setFilterToDate(e.target.value)}
            style={{ padding: '5px' }}
          />
        </div>

        <div>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Zip Code</label>
          <input 
            type="text" 
            placeholder="Filter by Zip" 
            value={filterZipCode} 
            onChange={e => setFilterZipCode(e.target.value)}
            style={{ padding: '5px' }}
          />
        </div>

        <div>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Attachments</label>
          <select value={filterHasAttachments} onChange={e => setFilterHasAttachments(e.target.value)} style={{ padding: '5px' }}>
            <option value="">All</option>
            <option value="yes">With Attachments</option>
            <option value="no">No Attachments</option>
          </select>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button 
            onClick={() => {
              setFilterJobType('');
              setFilterPaymentStatus('');
              setFilterJobStatus('');
              setFilterZipCode('');
              setFilterHasAttachments('');
              setFilterFromDate('');
              setFilterToDate('');
            }}
            style={{ padding: '6px 12px', cursor: 'pointer' }}
          >
            Clear Filters
          </button>
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button
            onClick={handleQuickbooksExport}
            disabled={exporting || !canExportQuickbooksCsv}
            title={canExportQuickbooksCsv ? 'Export CSV for QuickBooks import' : 'Disabled for this tenant plan'}
            style={{
              padding: '6px 12px',
              cursor: (exporting || !canExportQuickbooksCsv) ? 'default' : 'pointer',
              background: (exporting || !canExportQuickbooksCsv) ? '#d9d9d9' : '#539c98',
              color: (exporting || !canExportQuickbooksCsv) ? '#666' : '#fff',
              border: '1px solid #4c8f8b',
              borderRadius: '4px'
            }}
          >
            {exporting ? 'Exporting...' : 'Export QuickBooks CSV'}
          </button>
        </div>
      </div>

      {notice && (
        <div style={{ marginBottom: '10px', color: '#b42318', fontWeight: 600 }}>
          {notice}
        </div>
      )}

      {/* Results Summary */}
      <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>
        Found {filteredEvents.length} records. Total Price: ${totalRevenue.toFixed(2)}
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '800px' }}>
          <thead>
            <tr style={{ background: '#eee', borderBottom: '2px solid #ccc' }}>
              <th style={{ padding: '10px', textAlign: 'left' }}>Job Type</th>
              <th style={{ padding: '10px', textAlign: 'right' }}>Price</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Customer</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>PayStat</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Job Status</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Email</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Phone</th>
              <th style={{ padding: '10px', textAlign: 'left' }}>Zip Code</th>
              <th style={{ padding: '10px', textAlign: 'center' }}>Attachments</th>
            </tr>
          </thead>
          <tbody>
            {filteredEvents.length === 0 ? (
              <tr>
                <td colSpan="9" style={{ padding: '20px', textAlign: 'center' }}>No records found matching filters.</td>
              </tr>
            ) : (
              filteredEvents.map(ev => (
                <tr key={ev.id} style={{ borderBottom: '1px solid #ddd' }}>
                  <td style={{ padding: '10px' }}>{getJobDescription(ev.job_number)}</td>
                  <td style={{ padding: '10px', textAlign: 'right' }}>${Number(ev.negotiated_price || 0).toFixed(2)}</td>
                  <td style={{ padding: '10px' }}>{ev.last_name} - {ev.addr_street}</td>
                  <td style={{ padding: '10px' }}>
                    <span style={{ 
                      fontWeight: (ev.payment_status === 0 || ev.payment_status === 1) ? 'bold' : 'normal',
                      color: ev.payment_status === 0 ? 'red' : (ev.payment_status === 1 ? 'orange' : 'green')
                    }}>
                      {PAYMENT_STATUS_LABELS[ev.payment_status] || 'Unknown'}
                    </span>
                  </td>
                  <td style={{ padding: '10px' }}>{JOB_STATUS_LABELS[ev.job_status] || 'Unknown'}</td>
                  <td style={{ padding: '10px' }}>{ev.customer_email || '-'}</td>
                  <td style={{ padding: '10px' }}>{formatPhoneNumber(ev.phone_number)}</td>
                  <td style={{ padding: '10px' }}>{ev.addr_zip_code || '-'}</td>
                  <td style={{ padding: '10px', textAlign: 'center', fontWeight: 600 }} title={`${Number(ev.attachment_count || 0)} attachment(s)`}>
                    {Number(ev.attachment_count || 0) > 0 ? `📎 ${Number(ev.attachment_count || 0)}` : '-'}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default CustomerReport;
